<script setup>
// Painel de configuração do node Proteções (guardrails) — porte do navisuite.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const CHECKS = [
  {
    id: 'pii',
    label: 'Dados pessoais (PII)',
    desc: 'Bloqueia CPF, RG, cartão, endereço completo em mensagens',
  },
  {
    id: 'moderation',
    label: 'Moderação',
    desc: 'Ofensas, ameaças, conteúdo inadequado',
  },
  {
    id: 'jailbreak',
    label: 'Jailbreak',
    desc: 'Tentativa de fazer o agente sair do papel ("ignore as instruções...")',
  },
  { id: 'nsfw', label: 'Conteúdo NSFW', desc: 'Sexual/violento explícito' },
  {
    id: 'urls',
    label: 'Filtro de URLs',
    desc: 'Bloqueia links suspeitos / fora de allowlist',
  },
  {
    id: 'prompt_injection',
    label: 'Prompt injection',
    desc: 'Texto tentando manipular ferramentas internas',
  },
  {
    id: 'custom',
    label: 'Regra customizada',
    desc: 'Sua instrução: bloqueia se a mensagem violar',
  },
];

const checks = computed(() => props.data.guardChecks || []);
const isOn = id => checks.value.includes(id);

const toggle = id => {
  const set = new Set(checks.value);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  emit('update', { guardChecks: [...set] });
};
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-shield-check text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.FLOW.GUARD_TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.NAME') }}
    </label>
    <input
      :value="data.label"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="emit('update', { label: $event.target.value })"
    />

    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.GUARD_DESC') }}
    </p>

    <div class="flex flex-col gap-2">
      <div
        v-for="c in CHECKS"
        :key="c.id"
        class="flex items-start justify-between gap-2 rounded-lg border border-n-weak p-2.5"
      >
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">{{ c.label }}</p>
          <p class="truncate text-xs text-n-slate-10">{{ c.desc }}</p>
        </div>
        <button
          class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
          :class="isOn(c.id) ? 'bg-n-brand' : 'bg-n-slate-5'"
          @click="toggle(c.id)"
        >
          <span
            class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
            :class="isOn(c.id) ? 'left-[18px]' : 'left-0.5'"
          />
        </button>
      </div>
    </div>

    <textarea
      v-if="isOn('custom')"
      :value="data.customCheckPrompt"
      rows="3"
      :placeholder="t('AI_AGENTS.FLOW.GUARD_CUSTOM_PLACEHOLDER')"
      class="mt-2 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="emit('update', { customCheckPrompt: $event.target.value })"
    />

    <div
      class="mt-3 flex items-start justify-between gap-2 rounded-lg border border-n-weak p-2.5"
    >
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW.GUARD_CONTINUE') }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.GUARD_CONTINUE_DESC') }}
        </p>
      </div>
      <button
        class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
        :class="data.continueOnError ? 'bg-n-brand' : 'bg-n-slate-5'"
        @click="emit('update', { continueOnError: !data.continueOnError })"
      >
        <span
          class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
          :class="data.continueOnError ? 'left-[18px]' : 'left-0.5'"
        />
      </button>
    </div>

    <p class="mt-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.GUARD_OUTPUTS') }}
    </p>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
